/** Triangle soup in a model's own frame: xyz triples and triangle indices. */
export interface Triangles {
  positions: Float32Array;
  indices: Uint32Array;
}

interface GltfNode {
  mesh?: number;
  children?: number[];
  translation?: number[];
  rotation?: number[];
  scale?: number[];
  matrix?: number[];
}

interface GltfJson {
  scene?: number;
  scenes: { nodes: number[] }[];
  nodes: GltfNode[];
  meshes: {
    primitives: { attributes: { POSITION: number }; indices?: number }[];
  }[];
  accessors: {
    bufferView: number;
    byteOffset?: number;
    componentType: number;
    count: number;
  }[];
  bufferViews: { byteOffset?: number; byteStride?: number }[];
}

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;

/**
 * Reads every mesh triangle out of a GLB, with node transforms applied. Only what static collision
 * needs: positions and indices of the default scene; skins, morphs, and sparse accessors are ignored.
 */
export function readGlbTriangles(buffer: ArrayBuffer): Triangles {
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== GLB_MAGIC) throw new Error('not a GLB');

  let json: GltfJson | undefined;
  let bin: DataView | undefined;
  for (let offset = 12; offset < buffer.byteLength;) {
    const length = view.getUint32(offset, true);
    const type = view.getUint32(offset + 4, true);
    const start = offset + 8;
    if (type === CHUNK_JSON) {
      json = JSON.parse(
        new TextDecoder().decode(new Uint8Array(buffer, start, length)),
      );
    } else if (type === CHUNK_BIN) {
      bin = new DataView(buffer, start, length);
    }
    offset = start + length;
  }
  if (!json || !bin) throw new Error('GLB is missing its JSON or BIN chunk');

  const positions: number[] = [];
  const indices: number[] = [];
  const gltf = json;
  const data = bin;

  const visit = (nodeIndex: number, parent: number[]) => {
    const node = gltf.nodes[nodeIndex];
    const world = multiply(parent, localMatrix(node));
    if (node.mesh !== undefined) {
      for (const primitive of gltf.meshes[node.mesh].primitives) {
        const base = positions.length / 3;
        const points = readAccessor(
          gltf,
          data,
          primitive.attributes.POSITION,
          3,
        );
        for (let i = 0; i < points.length; i += 3) {
          const [x, y, z] = transformPoint(
            world,
            points[i],
            points[i + 1],
            points[i + 2],
          );
          positions.push(x, y, z);
        }
        const count = points.length / 3;
        const tris =
          primitive.indices !== undefined
            ? readAccessor(gltf, data, primitive.indices, 1)
            : Array.from({ length: count }, (_, i) => i);
        for (const index of tris) indices.push(base + index);
      }
    }
    for (const child of node.children ?? []) visit(child, world);
  };

  for (const root of gltf.scenes[gltf.scene ?? 0].nodes) visit(root, IDENTITY);
  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
  };
}

function readAccessor(
  gltf: GltfJson,
  bin: DataView,
  index: number,
  components: number,
): number[] {
  const accessor = gltf.accessors[index];
  const bufferView = gltf.bufferViews[accessor.bufferView];
  const size = componentSize(accessor.componentType);
  const stride = bufferView.byteStride ?? size * components;
  const start = (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
  const out: number[] = [];
  for (let i = 0; i < accessor.count; i++) {
    for (let c = 0; c < components; c++) {
      out.push(
        readComponent(
          bin,
          start + i * stride + c * size,
          accessor.componentType,
        ),
      );
    }
  }
  return out;
}

function componentSize(type: number): number {
  switch (type) {
    case 5121:
      return 1;
    case 5123:
      return 2;
    case 5125:
    case 5126:
      return 4;
    default:
      throw new Error(`unsupported accessor component type ${type}`);
  }
}

function readComponent(bin: DataView, offset: number, type: number): number {
  switch (type) {
    case 5121:
      return bin.getUint8(offset);
    case 5123:
      return bin.getUint16(offset, true);
    case 5125:
      return bin.getUint32(offset, true);
    default:
      return bin.getFloat32(offset, true);
  }
}

/** Column-major 4x4, as glTF stores it. */
const IDENTITY = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

function localMatrix(node: GltfNode): number[] {
  if (node.matrix) return node.matrix;
  const [tx, ty, tz] = node.translation ?? [0, 0, 0];
  const [x, y, z, w] = node.rotation ?? [0, 0, 0, 1];
  const [sx, sy, sz] = node.scale ?? [1, 1, 1];
  return [
    (1 - 2 * (y * y + z * z)) * sx,
    2 * (x * y + z * w) * sx,
    2 * (x * z - y * w) * sx,
    0,
    2 * (x * y - z * w) * sy,
    (1 - 2 * (x * x + z * z)) * sy,
    2 * (y * z + x * w) * sy,
    0,
    2 * (x * z + y * w) * sz,
    2 * (y * z - x * w) * sz,
    (1 - 2 * (x * x + y * y)) * sz,
    0,
    tx,
    ty,
    tz,
    1,
  ];
}

function multiply(a: number[], b: number[]): number[] {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += a[k * 4 + row] * b[col * 4 + k];
      out[col * 4 + row] = sum;
    }
  }
  return out;
}

function transformPoint(
  m: number[],
  x: number,
  y: number,
  z: number,
): number[] {
  return [
    m[0] * x + m[4] * y + m[8] * z + m[12],
    m[1] * x + m[5] * y + m[9] * z + m[13],
    m[2] * x + m[6] * y + m[10] * z + m[14],
  ];
}
